/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { of } from 'rxjs';
import { Area } from '@src/app/model/area';
import { STD_ANIMATION } from '@src/app/shared/std-animation';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { required } from '@src/app/shared/validators';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogOptions, PickDialogComponent } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { MailMergeService } from '@src/app/modules/agent/mail-merge.service';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { Core } from '@src/app/model/core';
import { AreaService } from '@src/app/modules/unit/area.service';
import { FormCheckboxComponent } from '@src/app/shared/form/form-checkbox/form-checkbox.component';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { CommsTemplateType } from '@src/app/model/CommsTemplate';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { MailMerge } from '@src/app/model/mailMerge';
import { CtlHolderComponent } from '@src/app/shared/form/ctl-holder/ctl-holder.component';
import { NgTemplateOutlet } from '@angular/common';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatTooltip } from '@angular/material/tooltip';
import { ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TenantService } from '@src/app/shared/tenant.service';
import { HttpClient} from '@angular/common/http';
import { MessageService } from '@src/app/shared/message.service';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { Field } from '@src/app/shared/field/Field';
import { AttachmentGridComponent } from '@src/app/shared/form/file/attachment-grid/attachment-grid.component';
import { RequestService } from '@src/app/modules/crm/request.service';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { SimpleRequestComponent } from '@src/app/modules/crm/simple-request/simple-request.component';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { Frequency } from '@src/app/model/frequency';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { MailMergeNavRoute } from '@src/app/routes/app-router';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';
import { ServiceRequestPageNavRoute } from '@src/app/routes/service-request-page-nav';
import { UnitType } from '@src/app/model/unit-type';
import { UnitTypeService } from '@src/app/modules/unit/unit-type-page/unit-type.service';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { PreviewMailDialogComponent } from '@src/app/modules/social/comms-template-page/preview-mail-dialog.component';
import { MailMergeRecipientsDialogComponent } from '@src/app/modules/agent/mail-merge-page/mail-merge-recipients-dialog.component';

@Component({
    selector: 'app-mail-merge-page',
    templateUrl: './mail-merge-page.component.html',
    styleUrls: ['./mail-merge-page.component.scss'],
    animations: [STD_ANIMATION],
    imports: [FormPageComponent, CtlHolderComponent, NgTemplateOutlet, MatRadioButton, MatIconModule,
        MatRadioGroup, MatTooltip, ReactiveFormsModule, RouterLink, MatButtonModule, MatProgressSpinnerModule]
})

export class MailMergePageComponent extends AbstractPageComponent {
    dataSvc = inject(MailMergeService);
    dialogRef = inject<MatDialogRef<MailMergePageComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });


    areaService = inject(AreaService);
    typeService = inject(UnitTypeService);
    requestService = inject(RequestService);
    router = inject(Router);
    http = inject(HttpClient);
    cds = inject(ConfirmDialogService);
    msgSvc = inject(MessageService);
    dialog = inject(MatDialog);

    submitting = false;
    loadingRecipients = false;

    areas: Area[] = [];
    cores: Core[] = [];

    ownerTip = $localize`Emails will be sent directly to owners where their email is on file`;
    userTip = $localize`PDF will be generated for you to print. The PDF will contain letters to be sent and statement if requested, a PDF will be requested for every 50 units`;
    noEmailToMeTip = $localize`Only invoices for units with no email address will be put in a PDF and mailed to you in groups of up to 50 at a time`;

    nameField = FieldMaker.nameControl({ validators: [required] }).override({
        label: 'Unique Name (subject for the mail)',
        formColumn: 2,
    });

    mailAttachmentGrid = AttachmentGridComponent.make(this.dataSvc)
        .override({showGrid: false, showAllFields: false, allowSimpleAdd: true, showSimple: true} as Partial<Field>).setupControl();

    contentField = FormRichTextComponent.make('Content', 'content', {
        formColumn: 2, validators: [required], cellOpts: {width: '80%', style: 'minWidth: 50%'},
        visible: Field.formOnly, richTextHeight: '500'
    }).setCommsTemplate(CommsTemplateType.PERSON);

    previewButton = FormButtonComponent.makeIconButton('Preview', 'preview',
        'Preview this merge against a sample: the first unit matching the current filters, and its first owner/resident. Nothing is sent or saved.',
        () => this.doPreview(), 'Preview')
        .override({ visible: Field.formOnly })
        .setupControl();

    testButton = FormButtonComponent.makeIconButton('Test', 'science',
        'Send yourself a test copy against the same sample. It will not go to any owner/resident, and nothing is recorded.',
        () => this.doTest(), 'Test')
        .override({ visible: Field.formOnly })
        .setupControl();

    dataButton = FormButtonComponent.makeIconButton('Data', 'table_view',
        'Run the full matching against the current filters and show every recipient who would receive mail, without sending or saving anything. May take a while for large OMCs.',
        () => this.doData(), 'Preview Data')
        .override({ visible: Field.formOnly })
        .setupControl();

    /* Two distinct buttons, not one relabelled - the icon is a clearer cue than text alone that the
     * destination radio choice above actually took, since it's easy to forget to set it. */
    submitEmailButton = FormButtonComponent.makeIconButton('Send Mails', 'send',
        'Run this mail merge for real - sends emails to matching owners/residents', () => this.submitMerge(), 'Send Mails')
        .override({ visible: Field.formOnly })
        .setupControl();

    submitPdfButton = FormButtonComponent.makeIconButton('Generate PDF', 'picture_as_pdf',
        'Run this mail merge for real - generates the PDF batch for matching owners/residents', () => this.submitMerge(), 'Generate PDF')
        .override({ visible: Field.formOnly })
        .setupControl();

    coreField = FormPicklistComponent.make('Core', 'params.coreId', null, { items: [], allowSelectNone: true }, {
        formRow: 2, formColumn: 2, visible: Field.formOnly
    });

    areaField = FormPicklistComponent.make('Area', 'params.areaId', 'area', {
        refreshes: [(area: Area) => {
            const currentCoreId = this.coreField.control.value;
            if (currentCoreId && !area.cores.find(c => c.id === currentCoreId)) {
                this.coreField.control.setValue(null);
            }
            this.coreField.setPicklistItems(area.cores);
        }], items: [], allowSelectNone: true }, {
            formRow: 2
        }
    );

    categoryField = FormPicklistComponent.make('Category', 'params.categoryId', null, { items: [], allowSelectNone: true }, {
        formRow: 2, formColumn: 2, visible: Field.formOnly
    });

    typeField = FormPicklistComponent.make('Type', 'params.typeId', 'type', {
        refreshes: [(type: UnitType) => {
            const currentCategoryId = this.categoryField.control.value;
            if (currentCategoryId && !type.categories.find(c => c.id === currentCategoryId)) {
                this.categoryField.control.setValue(null);
            }
            this.categoryField.setPicklistItems(type.categories);
        }], items: [], allowSelectNone: true }, {
            formRow: 2
        }
    );    

    frequencyField = FormPicklistComponent.make('Frequency', 'params.frequencyId', null, { items: Frequency.PAYMENT_OPTIONS, allowSelectNone: true }, {
        formRow: 2, formColumn: 2, visible: Field.formOnly
    });
    lastPayBeforeField = FormDateComponent.make('Last Payment Before', 'params.lastPayBefore');

    targetField = FormPicklistComponent.make('Target', 'params.targetId', null, {
        items: [ {id: 'owners', name: 'Owners (Resident or Not)'}, {id: 'residents', name: 'Residents (owner or not)'} ],
        refreshes: [(o) => {
            if (o.id === 'owners') {
                this.includeStatementField.enableControl();
                this.filterArrearsField.enableControl();
            } else {
                this.includeStatementField.control.setValue(false);
                this.includeStatementField.disableControl();
                this.filterArrearsField.disableControl();
                this.filterArrearsField.control.setValue(false);
            }
        }]
    }, {validators: [required]})


    filterArrearsField = FormCheckboxComponent.make('Filter with arrears', 'params.filterArrears', {
        valueChanges: (val) => {
            console.log(val);
            if (val) {
                this.minArrearsField.enableControl();
            } else {
                this.minArrearsField.control.setValue(0);
                this.minArrearsField.disableControl();
            }
        }, formRow: 3
    });

    includeStatementField = FormCheckboxComponent.make('Include Statement?', 'params.includeStatement', { formRow: 3, formColumn: 2 })

    minArrearsField = FormNumberComponent.makeCurrency('Minimum Arrears', 'params.minArrears', {
        disable: true, formColumn: 3, formRow: 3, visible: Field.formOnly
    });

    statusField = FormTextComponent.make('Status', 'status', {visible: Field.noForm});

    attachmentGrid = AttachmentGridComponent.make(this.dataSvc).setupControl();

    destinationField = FormTextComponent.make('Destination', 'destination', {visible: Field.noForm, readonly: true});
    //fg = new FormGroup({destination: this.destinationField.makeControl()});

    mailMerge: MailMerge = new MailMerge();

    config = new FormConfig({
        navRoute: MailMergeNavRoute,
        docPath: 'omc-community/managers/mail-merge',
        title: $localize`Mail Merges`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                this.nameField,
                this.statusField,
                this.contentField,
                this.previewButton,
                this.testButton,
                this.dataButton,
                this.submitEmailButton,
                this.submitPdfButton,
                this.targetField,
                this.includeStatementField,
                //this.areaField,
                //this.coreField,
                //this.typeField,
                //this.categoryField,
                this.frequencyField,
                this.lastPayBeforeField,
                this.filterArrearsField,
                this.minArrearsField,
                this.destinationField,
                FieldMaker.createdAt(),
                FieldMaker.updatedAt()
            ],
            formLayout: []
            /*formLayout: [
                { cells: [{ colspan: 3 }, { width: '66%', rowspan: 5 }] },
                { cells: [{ width: '16%', colspan: 2 }, { width: '16%' }]},
                { cells: [{ width: '8%' }, { width: '8%' }, { width: '16%' }] },
                { cells: [{ colspan: 3 }] },
            ]*/,
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.newMerge.bind(this),
        beforeEdit: this.beforeEdit.bind(this),
        fixedFormHeight: false,
    });

    constructor() {
        super();

        this.areaService.get<Area>().subscribe( areas => {
            this.areaField.setPicklistItems(areas);
            this.coreField.setPicklistItems(this.areaService.getCores(areas));
        });

        this.typeService.get<UnitType>().subscribe( type => {
            this.typeField.setPicklistItems(type);
            this.categoryField.setPicklistItems(this.typeService.getCategories(type));
        })        
    }

    newMerge() {
        this.mailMerge = new MailMerge();
        this.mailMerge.status = 'New';
        this.mailMerge.params = {}
        this.mailMerge.params.targetId = 'owners';
        this.mailMerge.params.filterArrears = false;
        this.mailMerge.params.includeStatement = false;
        this.mailMerge.params.minArrears = 0;
        this.mailMerge.destination = 'PDF';
        this.config.fieldSet.fields.forEach(f => f.setReadonly(false));
        this.attachmentGrid.setValue(this.mailMerge, true);
        return of(this.mailMerge)
    }

    unlinkRequest() {
        const dtl = $localize `Stop linking responses to this mailing to request
        ${this.mailMerge.request?.teamId}-${this.mailMerge.request?.refNr} : ${this.mailMerge.request?.title}`;
        this.cds.open('Unlink Request', dtl, () => {
            this.dataSvc.linkRequest(this.mailMerge, null).subscribe( (m) => {
                if (m) {
                    this.mailMerge.mergeRequestId = null;
                    this.mailMerge.request = null;
                }
            });
        })
    }

    createRequest() {
        // The dialog's source field is hidden, so the caller has to name one - the work here always
        // arises from correspondence the office is sending.
        const data = { requestSource: ServiceRequest.srcEmailManual.id };
        this.dialog.open(SimpleRequestComponent, {data}).afterClosed().subscribe( sr => {
            if (sr) {
                if (this.mailMerge.id) {
                    this.linkRequest(sr);
                }
            }
        })
    }

    chooseRequest() {
        const chooseRequestConfig = new FormConfig({
            fieldSet: new FieldSet({
                fields: [
                    FormTextComponent.make('Reference', 'refNr'),
                    FormTextComponent.make('Title', 'title'),
                ],
                formLayout: LAYOUT_OPTIONS.singleCol,
            }),
            title: $localize`Suppliers`,
            mode: 'list',
        });

        const dialogRef = this.dialog.open(PickDialogComponent,
            {
                data: {
                    config: chooseRequestConfig,
                    service: this.requestService,
                    showSearch: true,
                }
            });
        dialogRef.afterClosed().subscribe( sr => {
            if (sr) {
                const msg = $localize `Link incoming responses to request ${sr.refNr} : ${sr.title}`;
                this.cds.open($localize `Link Request`, msg, () => {
                    console.log('linker up there');
                   this.linkRequest(sr);
                })
            }
        });
    }

    linkRequest(sr: ServiceRequest) {
        this.dataSvc.linkRequest(this.mailMerge, sr).subscribe( (m) => {
            if (m) {
                this.mailMerge = this.beforeEdit(m);
                this.page.form.reloadContent(this.mailMerge);
            }
        });
    }

    navigateRequest() {
        if (this.mailMerge.mergeRequestId) {
            this.router.navigate([ServiceRequestPageNavRoute.getIdUrl(this.mailMerge.mergeRequestId)]);
        }
    }

    beforeEdit(o: MailMerge) {
        if (!o) {
            o = new MailMerge();
        }
        this.mailMerge = o
        this.attachmentGrid.override({ showSimple: true, readonly: true, showGrid: false } as Partial<Field>);
        if (o.status === 'New') {
            this.mailAttachmentGrid.setValue(o, false);
        } else {
            this.attachmentGrid.setValue(o, true);
        }

        if (o.status === 'New' || !o.status ) {
            this.config.fieldSet.fields.forEach(f => f.setReadonly(false));
            this.destinationField.control.enable()
        } else {
            this.config.fieldSet.fields.forEach ( f => f.setReadonly(true));
            this.destinationField.control.disable()
        }
        if (o.params?.filterArrears) {
            this.minArrearsField.enableControl();
        } else {
            this.minArrearsField.disableControl();
        }
        return o;
    }

    getUnitsLink() {
        return UnitPageNavRoute.getListRouterLink({ mergeId: this.mailMerge.id });
    }

    getPeopleLink() {
        return PersonPageNavRoute.getListRouterLink({ mergeId: this.mailMerge.id });
    }

    doPreview() {
        const current = this.config.fieldSet.getFormValue() as MailMerge;
        this.dataSvc.preview(current.content, current.name, current.destination, current.params)
            .subscribe(result => {
                if (result) {
                    this.dialog.open(PreviewMailDialogComponent, { data: { title: $localize`Preview`, html: result.html }, width: '80vw', maxWidth: '80vw' });
                }
            });
    }

    doTest() {
        const current = this.config.fieldSet.getFormValue() as MailMerge;
        this.dataSvc.sendTest(current.content, current.name, current.destination, current.params)
            .subscribe(result => {
                if (result) {
                    this.msgSvc.show($localize`Test sent — check your inbox.`);
                }
            });
    }

    doData() {
        const current = this.config.fieldSet.getFormValue() as MailMerge;
        this.loadingRecipients = true;
        this.dataButton.disable = true;
        this.dataSvc.previewRecipients(current.content, current.name, current.destination, current.params)
            .subscribe(result => {
                this.loadingRecipients = false;
                this.dataButton.disable = false;
                if (result) {
                    this.dialog.open(MailMergeRecipientsDialogComponent,
                        { data: { title: $localize`Recipients`, recipients: result.recipients, summary: result.summary }, width: '80vw', maxWidth: '80vw' });
                }
            });
    }

    submitMerge() {

        const url = TenantService.ajaxPath + `mailmerge/${this.mailMerge.id}/${this.mailMerge.revision}/submit`;
        this.setSubmitting(true);

        this.http.put<MailMerge>(url, null).subscribe(x => {
            if (x) {
                this.mailMerge = x;
            }
            this.setSubmitting(false);
        }, error => {
            this.msgSvc.showError(error, 'Submitting MailMerge Job');
        })
    }

    private setSubmitting(submitting: boolean) {
        this.submitting = submitting;
        this.submitEmailButton.disable = submitting;
        this.submitPdfButton.disable = submitting;
    }
}
